// 計薪 / 薪資單 hooks (H3b)。員工看自己的、人資產生/管理。
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DevActorPreset } from './api.ts';
import { type ApiError, api } from './api.ts';
import type { RemitItem } from './statutory-remit.ts';

export interface PayslipListRow {
  id: string;
  user_id?: string;
  name?: string;
  period_year: number;
  period_month: number;
  status: 'draft' | 'finalized';
  gross_pay: string;
  total_deduction: string;
  net_pay: string;
}

export interface PayslipLine {
  line_no: number;
  code: string;
  label: string;
  kind: 'earning' | 'deduction' | 'employer';
  amount: string;
}
export interface PayslipDetail {
  id: string;
  user_id: string;
  name: string;
  employee_code: string | null;
  title_name: string | null;
  department: string | null;
  hire_date: string | null;
  period_year: number;
  period_month: number;
  status: 'draft' | 'finalized';
  gross_pay: string;
  total_deduction: string;
  net_pay: string;
  labor_insured: string;
  nhi_insured: string;
  pension_wage: string;
  employer_labor: string;
  employer_nhi: string;
  employer_pension: string;
  /** V308: 雇主端二代健保補充保費 (逐人分攤) */
  employer_supp_nhi: string;
  generated_at: string;
  finalized_at: string | null;
  /** V337: 公司發薪日 (每月幾號); 薪資單「發放日期」用。 */
  pay_day_of_month: number | null;
  lines: PayslipLine[];
}

export function useMyPayslips(actor: DevActorPreset) {
  return useQuery<PayslipListRow[], ApiError>({
    queryKey: ['my-payslips', actor],
    queryFn: () => api<PayslipListRow[]>('/api/v1/me/payslips', { actor }),
  });
}

export function usePayslip(id: string | undefined, actor: DevActorPreset) {
  return useQuery<PayslipDetail, ApiError>({
    queryKey: ['payslip', id, actor],
    queryFn: () => api<PayslipDetail>(`/api/v1/payroll/payslips/${id}`, { actor }),
    enabled: !!id,
  });
}

/**
 * 單張薪資條的**信件**預覽 —— 人資定稿時看的就是員工會收到的那一封。
 * 走後端 renderOne（跟 notify / notify-preview 同一條），不是另外組一份。
 */
export interface PayslipEmailPreview {
  name: string;
  email: string | null;
  status: string;
  notified_at: string | null;
  subject: string;
  html: string;
}
export function usePayslipEmailPreview(id: string | undefined, actor: DevActorPreset) {
  return useQuery<PayslipEmailPreview, ApiError>({
    queryKey: ['payslip-email-preview', id, actor],
    queryFn: () =>
      api<PayslipEmailPreview>(`/api/v1/payroll/payslips/${id}/email-preview`, { actor }),
    enabled: !!id,
  });
}

export function usePayrollPayslips(actor: DevActorPreset, year: number, month: number) {
  return useQuery<PayslipListRow[], ApiError>({
    queryKey: ['payroll-payslips', actor, year, month],
    queryFn: () =>
      api<PayslipListRow[]>(`/api/v1/payroll/payslips?year=${year}&month=${month}`, { actor }),
  });
}

export interface GeneratePayslipBody {
  user_id: number;
  year: number;
  month: number;
  bonus?: number;
  bonus_label?: string;
  income_tax?: number;
  bonus_tax_rate?: number; // 獎金就源預扣稅率(%),如 5
  tax_adjustment?: number; // 前期代扣稅差額 (正 = 扣、負 = 退)
  tax_adjustment_note?: string;
}
export function useGeneratePayslip(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<{ id: number }, ApiError, GeneratePayslipBody>({
    mutationFn: (body) => api('/api/v1/payroll/generate', { actor, method: 'POST', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payslips'] });
      // 轉帳明細與國泰薪轉檔讀的是同一批薪資單 (實發/狀態) → 一起重抓, 否則重開 / 重算後
      // 畫面還停在舊金額 (2026-10-19 使用者踩到)。
      qc.invalidateQueries({ queryKey: ['payroll-bank-transfer'] });
      qc.invalidateQueries({ queryKey: ['payroll-cathay-preview'] });
      qc.invalidateQueries({ queryKey: ['payroll-monthly-summary'] });
    },
  });
}

export function useGenerateAllPayslips(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<
    {
      total: number;
      created: number;
      updated: number;
      /** 已定版, 不動。 */
      skipped: number;
      /** 2026-08-23: 該月尚未到職 —— 原本跟 skipped 併成同一個數字, 於是被講成
       *  「已定版跳過」。 拆開之後各自回報。 */
      not_hired: number;
      /** 本薪 0 且本月無獎金/年終/里程碑 → 沒東西可發, 不產。 */
      no_pay: number;
      milestones: number;
      /** 沒產出的人分別是誰 —— 只給數字的話使用者得自己猜。 */
      skipped_names?: string[];
      not_hired_names?: string[];
      no_pay_names?: string[];
    },
    ApiError,
    { year: number; month: number }
  >({
    mutationFn: (body) =>
      api('/api/v1/payroll/generate-all', { actor, method: 'POST', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payslips'] });
      qc.invalidateQueries({ queryKey: ['payroll-bank-transfer'] });
      qc.invalidateQueries({ queryKey: ['payroll-cathay-preview'] });
      qc.invalidateQueries({ queryKey: ['payroll-monthly-summary'] });
    },
  });
}

export function useFinalizePayslip(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<{ id: number }, ApiError, string>({
    mutationFn: (id) =>
      api(`/api/v1/payroll/payslips/${id}/finalize`, { actor, method: 'POST', json: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payslips'] });
      qc.invalidateQueries({ queryKey: ['payroll-bank-transfer'] });
      qc.invalidateQueries({ queryKey: ['payroll-cathay-preview'] });
      qc.invalidateQueries({ queryKey: ['payslip'] });
      qc.invalidateQueries({ queryKey: ['payroll-monthly-summary'] });
    },
  });
}

// 刪除草稿薪資單 (誤產生的, 如未到職者留下的孤兒草稿)。 已定版的後端擋 409。
export function useDeletePayslip(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ApiError, string>({
    mutationFn: (id) => api(`/api/v1/payroll/payslips/${id}`, { actor, method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payslips'] });
      qc.invalidateQueries({ queryKey: ['payroll-bank-transfer'] });
      qc.invalidateQueries({ queryKey: ['payroll-cathay-preview'] });
      qc.invalidateQueries({ queryKey: ['payroll-monthly-summary'] });
    },
  });
}

export interface Compensation {
  effective_from: string;
  engagement_type: 'full_time' | 'part_time';
  base_salary: string;
  allowances: string;
  meal_allowance: string;
  salary_months: string;
  dependents_nhi: number;
  dependents_tax: number;
  voluntary_pension_rate: string;
  enrolled_labor: boolean;
  enrolled_nhi: boolean;
  insured_salary_override: string | null;
  income_tax_override: string | null;
  /** V308: 自訂津貼細項; 空陣列 = 只用 allowances 合計 (舊資料) */
  // 2026-07-19: 每筆帶稅務處理 (省略 = 應稅 + 計入投保, 向下相容舊資料)
  allowance_items: { label: string; amount: number; taxable?: boolean; insurable?: boolean }[];
}

export function useCompensation(userId: string | number | undefined, actor: DevActorPreset) {
  return useQuery<Compensation | null, ApiError>({
    queryKey: ['compensation', String(userId), actor],
    queryFn: () =>
      api<Compensation | null>(`/api/v1/payroll/compensation?user_id=${userId}`, { actor }),
    enabled: !!userId,
  });
}

export function useSaveCompensation(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ApiError, Record<string, unknown>>({
    mutationFn: (body) => api('/api/v1/payroll/compensation', { actor, method: 'PUT', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compensation'] });
      qc.invalidateQueries({ queryKey: ['payroll-comp-list'] });
    },
  });
}

/**
 * 該期間的薪資有沒有過帳到總帳。
 *
 * STEP 1-4 的其他狀態都是前端從薪資單推導的，只有這一步得問後端 —— 過帳的結果
 * 在總帳那一側，薪資單上沒有任何痕跡。 can_check=false = 這個人沒有總帳的讀取
 * 權限（純 HR），那時**不要**宣稱已過或未過。
 */
export interface PayrollGlStatus {
  can_check: boolean;
  posted: boolean;
  entry_no: string | null;
  entry_date?: string | null;
}
export function usePayrollGlStatus(actor: DevActorPreset, year: number, month: number) {
  return useQuery<PayrollGlStatus, ApiError>({
    queryKey: ['payroll-gl-status', actor, year, month],
    queryFn: () => api(`/api/v1/payroll/gl-status?year=${year}&month=${month}`, { actor }),
  });
}

/** 一個期間的三個里程碑：過帳 / 轉帳 / 通知。 取代只回過帳的 gl-status。 */
export interface PayrollPeriodStatus {
  gl: { can_check: boolean; posted: boolean; entry_no: string | null; entry_date: string | null };
  transferred: {
    done_on: string;
    done_at: string;
    by_name: string | null;
    /** V391 匯款手續費（雇主負擔；同行轉帳通常為 0）。 */
    bank_fee: string | null;
    /** V448 預約中：薪資條排定寄出的日期（還沒寄）；null = 不是預約或已寄出。 */
    payslips_scheduled_on: string | null;
  } | null;
  total: number;
  finalized: number;
  notified: number;
  /** 轉帳完成時系統會不會自動寄薪資條（PAYSLIP_AUTO_NOTIFY，可用環境變數關掉）。 */
  auto_notify: boolean;
}
export function usePayrollPeriodStatus(actor: DevActorPreset, year: number, month: number) {
  return useQuery<PayrollPeriodStatus, ApiError>({
    queryKey: ['payroll-period-status', actor, year, month],
    queryFn: () => api(`/api/v1/payroll/period-status?year=${year}&month=${month}`, { actor }),
  });
}

export function useMarkTransferred(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<
    /** V392+: 轉帳完成時系統自動寄薪資條 → 回報寄了幾張、哪幾張沒寄成功。 */
    {
      ok: true;
      notified: { sent: number; skipped: { name: string; reason: string }[] } | null;
      /** V448 轉帳日期在未來 → 薪資條排定在這天寄（這次沒有寄）。 */
      scheduled_on: string | null;
    },
    ApiError,
    { year: number; month: number; done_on: string; note?: string | null; bank_fee?: number }
  >({
    mutationFn: (body) =>
      api('/api/v1/payroll/mark-transferred', { actor, method: 'POST', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-period-status'] });
      qc.invalidateQueries({ queryKey: ['payroll-todo'] });
    },
  });
}

/** V448 取消預約轉帳（只有預約中、薪資條還沒寄出的可以取消）。 */
export function useCancelScheduledTransfer(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { year: number; month: number }>({
    mutationFn: (body) =>
      api('/api/v1/payroll/cancel-transferred', { actor, method: 'POST', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-period-status'] });
      qc.invalidateQueries({ queryKey: ['payroll-todo'] });
    },
  });
}

/** 薪資條通知：預覽（收件人 / 主旨 / 完整 HTML）→ 人工按下才寄。 */
export interface PayslipNotifyPreviewItem {
  payslip_id: string;
  name: string;
  email: string | null;
  notified_at: string | null;
  subject: string;
  html: string;
}
export function usePayslipNotifyPreview(
  actor: DevActorPreset,
  year: number,
  month: number,
  enabled: boolean,
) {
  return useQuery<{ items: PayslipNotifyPreviewItem[] }, ApiError>({
    queryKey: ['payroll-notify-preview', actor, year, month],
    queryFn: () => api(`/api/v1/payroll/notify-preview?year=${year}&month=${month}`, { actor }),
    enabled,
  });
}
export function useSendPayslipNotify(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<
    { sent: number; skipped: { name: string; reason: string }[] },
    ApiError,
    { year: number; month: number; payslip_ids?: number[] }
  >({
    mutationFn: (body) => api('/api/v1/payroll/notify', { actor, method: 'POST', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-period-status'] });
      qc.invalidateQueries({ queryKey: ['payroll-notify-preview'] });
      qc.invalidateQueries({ queryKey: ['payroll-todo'] });
    },
  });
}

/** 薪資待辦：草稿待覆核 / 該轉帳了 / 該通知了（轉帳提醒發放日前 2 個工作天出現，遇假日自動提前）。 */
export interface PayrollTodoItem {
  kind: 'review' | 'transfer' | 'notify';
  year: number;
  month: number;
  count: number;
  amount?: string;
  pay_date?: string;
  due_on?: string;
  /**
   * 申請日期 = 這個期間的薪資流程起跑那天 (草稿產出來的日子)。
   * 2026-09-12 使用者:「薪資 現在是發薪日前幾天會起流程 那天就是申請日」——
   * 同一個期間的三種待辦 (待覆核 / 待轉帳 / 待寄薪資條) 共用同一個申請日期。
   */
  started_on?: string;
}
export function usePayrollTodo(actor: DevActorPreset, enabled: boolean) {
  return useQuery<{ items: PayrollTodoItem[] }, ApiError>({
    queryKey: ['payroll-todo', actor],
    queryFn: () => api('/api/v1/payroll/todo', { actor }),
    enabled,
    refetchInterval: 60_000,
  });
}

export function usePostPayrollGl(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<
    { entry_id: number; count: number; entry_no: string },
    ApiError,
    { year: number; month: number }
  >({
    mutationFn: (body) => api('/api/v1/payroll/post-gl', { actor, method: 'POST', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payslips'] });
      qc.invalidateQueries({ queryKey: ['payroll-bank-transfer'] });
      qc.invalidateQueries({ queryKey: ['payroll-cathay-preview'] });
      qc.invalidateQueries({ queryKey: ['payroll-gl-status'] });
    },
  });
}

/** V392 主流程：全部草稿定版 ＋ 過帳總帳，同一個交易（期間鎖定時兩件事都不發生）。 */
export function useFinalizeAndPost(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<
    { finalized_now: number; count: number; entry_id: number; entry_no: string | null },
    ApiError,
    { year: number; month: number }
  >({
    mutationFn: (body) =>
      api('/api/v1/payroll/finalize-and-post', { actor, method: 'POST', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payslips'] });
      qc.invalidateQueries({ queryKey: ['payroll-bank-transfer'] });
      qc.invalidateQueries({ queryKey: ['payroll-cathay-preview'] });
      qc.invalidateQueries({ queryKey: ['payslip'] });
      qc.invalidateQueries({ queryKey: ['payroll-monthly-summary'] });
      qc.invalidateQueries({ queryKey: ['payroll-gl-status'] });
      qc.invalidateQueries({ queryKey: ['payroll-period-status'] });
      qc.invalidateQueries({ queryKey: ['payroll-todo'] });
    },
  });
}

/**
 * 重開定版 (2026-10-19)：已定版的薪資單退回草稿、作廢那張自動薪資分錄，之後照一般流程
 * 重算 → 定版並過帳。 只限還沒轉帳、薪資條還沒寄的期間（後端擋）。
 */
export function useReopenPayroll(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<
    { reopened: number; voided_entries: string[] },
    ApiError,
    { year: number; month: number }
  >({
    mutationFn: (body) => api('/api/v1/payroll/reopen', { actor, method: 'POST', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payslips'] });
      qc.invalidateQueries({ queryKey: ['payroll-bank-transfer'] });
      qc.invalidateQueries({ queryKey: ['payroll-cathay-preview'] });
      qc.invalidateQueries({ queryKey: ['payslip'] });
      qc.invalidateQueries({ queryKey: ['payroll-monthly-summary'] });
      qc.invalidateQueries({ queryKey: ['payroll-gl-status'] });
      qc.invalidateQueries({ queryKey: ['payroll-period-status'] });
      qc.invalidateQueries({ queryKey: ['payroll-todo'] });
    },
  });
}

// ---- 月份總覽 (各月薪資單彙總,排排站 + 下鑽) ----
export interface MonthlySummary {
  period_year: number;
  period_month: number;
  count: number;
  finalized_count: number;
  /** 已寄出薪資條的張數（判斷「這個月做完了沒」用）。 */
  notified_count: number;
  /** 已完成轉帳的日期；null = 還沒轉。 */
  transferred_on: string | null;
  gross_total: string;
  deduction_total: string;
  net_total: string;
}
export function useMonthlySummary(actor: DevActorPreset) {
  return useQuery<MonthlySummary[], ApiError>({
    queryKey: ['payroll-monthly-summary', actor],
    queryFn: () => api<MonthlySummary[]>('/api/v1/payroll/monthly-summary', { actor }),
  });
}

// ---- 薪資結構清單 (全員 + 現行薪資結構) ----
export interface CompListRow {
  user_id: string;
  name: string;
  base_salary: string | null;
  allowances: string | null;
  meal_allowance: string | null;
  engagement_type: string | null;
  salary_months: string | null;
  effective_from: string | null;
}
export function useCompList(actor: DevActorPreset) {
  return useQuery<CompListRow[], ApiError>({
    queryKey: ['payroll-comp-list', actor],
    queryFn: () => api<CompListRow[]>('/api/v1/payroll/comp-list', { actor }),
  });
}

// ---- 銀行轉帳明細表 (某月;含解密薪資帳號,hr/系統管理員) ----
export interface BankTransferRow {
  user_id: string;
  name: string;
  net_pay: string;
  status: string;
  salary_bank_name: string | null;
  salary_bank_branch: string | null;
  salary_account_holder: string | null;
  account: string | null;
  account_mask: string | null;
}
export function useBankTransfer(
  actor: DevActorPreset,
  year: number,
  month: number,
  enabled: boolean,
) {
  return useQuery<
    { year: number; month: number; rows: BankTransferRow[]; total_net: string },
    ApiError
  >({
    queryKey: ['payroll-bank-transfer', actor, year, month],
    queryFn: () => api(`/api/v1/payroll/bank-transfer?year=${year}&month=${month}`, { actor }),
    enabled,
  });
}

// ---- 國泰薪資撥轉上傳檔 (某月;預覽哪些人會進檔案) ----
export interface CathayPreviewRow {
  user_id: string;
  name: string;
  amount: number;
  bank_name: string | null;
  account_mask: string | null;
  /** ok = 進檔案；excluded = 不進檔案、要另外轉；error = 擋下載、要先修 */
  status: 'ok' | 'excluded' | 'error';
  problems: string[];
}
export interface CathayPreview {
  year: number;
  month: number;
  due_date: string;
  /** 讀不到公司銀行帳戶（純人資沒有權限）時是 null */
  payer_default: { account: string; tax_id: string; name: string } | null;
  rows: CathayPreviewRow[];
  ok_count: number;
  ok_total: number;
}
export function useCathayPreview(
  actor: DevActorPreset,
  year: number,
  month: number,
  enabled: boolean,
) {
  return useQuery<CathayPreview, ApiError>({
    queryKey: ['payroll-cathay-preview', actor, year, month],
    queryFn: () =>
      api(`/api/v1/payroll/cathay-salary-file/preview?year=${year}&month=${month}`, { actor }),
    enabled,
  });
}

// ---- 公司負擔彙總表 (某月雇主勞健保 + 勞退) ----
export interface EmployerCostRow {
  user_id: string;
  name: string;
  employer_labor: string;
  employer_nhi: string;
  employer_pension: string;
  employer_supp_nhi: string;
  employer_total: string;
}
export interface EmployerCostTotals {
  employer_labor: string;
  employer_nhi: string;
  employer_pension: string;
  employer_supp_nhi: string;
  employer_total: string;
}
export function useEmployerCost(
  actor: DevActorPreset,
  year: number,
  month: number,
  enabled: boolean,
) {
  return useQuery<
    { year: number; month: number; rows: EmployerCostRow[]; totals: EmployerCostTotals },
    ApiError
  >({
    queryKey: ['payroll-employer-cost', actor, year, month],
    queryFn: () => api(`/api/v1/payroll/employer-cost?year=${year}&month=${month}`, { actor }),
    enabled,
  });
}

// ---- 智慧調保提醒 ----
export interface InsuranceReviewItem {
  user_id: string;
  name: string;
  // V325: 勞保 / 健保 各自的目前申報值
  reported_labor: number | null;
  reported_nhi: number | null;
  suggested_labor: number;
  suggested_nhi: number;
  insured_base: number;
  /** V441: 薪酬設定有沒有勾加保 —— 「沒加保」和「加了但還沒申報」要分得出來 */
  enrolled_labor: boolean;
  enrolled_nhi: boolean;
  /** ok = 投保級距跟現行薪資一致, 不需要申報 (清單用; 提醒區要自己 filter 掉) */
  status: 'need_enroll' | 'need_adjust' | 'ok';
  upcoming_from?: string | null;
  last_event: { event_type: string; effective_date: string } | null;
}
export function useInsuranceReview(actor: DevActorPreset) {
  return useQuery<{ period: string; items: InsuranceReviewItem[] }, ApiError>({
    queryKey: ['payroll-insurance-review', actor],
    queryFn: () => api('/api/v1/payroll/insurance-review', { actor }),
  });
}

// ---- 年度各類所得扣繳憑單 (薪資所得) ----
export interface WithholdingRow {
  user_id: string;
  name: string;
  months: number;
  gross_total: string;
  tax_total: string;
  labor_self_total: string;
  nhi_self_total: string;
}
export function useWithholdingStatements(actor: DevActorPreset, year: number) {
  return useQuery<{ year: number; rows: WithholdingRow[] }, ApiError>({
    queryKey: ['payroll-withholding', year, actor],
    queryFn: () =>
      api<{ year: number; rows: WithholdingRow[] }>(
        `/api/v1/payroll/withholding-statements?year=${year}`,
        { actor },
      ),
  });
}

// ---- HR 設定 (年終發放月份 + 薪資發薪日) ----
export interface HrConfig {
  year_end_bonus_month: number;
  pay_day_of_month: number;
}
export function useHrConfig(actor: DevActorPreset) {
  return useQuery<HrConfig, ApiError>({
    queryKey: ['hr-config', actor],
    queryFn: () => api<HrConfig>('/api/v1/payroll/hr-config', { actor }),
  });
}
export function useUpdateHrConfig(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ApiError, Partial<HrConfig>>({
    mutationFn: (patch) =>
      api('/api/v1/payroll/hr-config', { actor, method: 'PATCH', json: patch }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-config'] });
      qc.invalidateQueries({ queryKey: ['payroll-payslips'] });
      qc.invalidateQueries({ queryKey: ['payroll-bank-transfer'] });
      qc.invalidateQueries({ queryKey: ['payroll-cathay-preview'] });
    },
  });
}

// ---- 里程碑 / 一次性給付 (簽約金分期/獎金/特休折算) ----
export type PayoutType =
  | 'signing_bonus'
  | 'performance_bonus'
  | 'annual_bonus'
  | 'leave_encashment'
  | 'other';
export const PAYOUT_TYPE_LABELS: Record<PayoutType, string> = {
  signing_bonus: '簽約金',
  performance_bonus: '績效獎金',
  annual_bonus: '年終獎金',
  leave_encashment: '特休折算',
  other: '其他',
};
export interface Payout {
  id: string;
  user_id: string;
  name: string;
  payout_type: PayoutType;
  name_zh: string | null;
  amount: string;
  scheduled_date: string | null;
  condition: string | null;
  is_recurring_income: boolean;
  status: 'pending' | 'released' | 'cancelled';
  released_date: string | null;
  note: string | null;
}
export function usePayouts(actor: DevActorPreset) {
  return useQuery<Payout[], ApiError>({
    queryKey: ['payouts', actor],
    queryFn: () => api<Payout[]>('/api/v1/payroll/payouts', { actor }),
  });
}
export function useCreatePayout(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<{ id: number }, ApiError, Record<string, unknown>>({
    mutationFn: (body) => api('/api/v1/payroll/payouts', { actor, method: 'POST', json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payouts'] }),
  });
}
export function useUpdatePayout(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ApiError, { id: string; patch: Record<string, unknown> }>({
    mutationFn: ({ id, patch }) =>
      api(`/api/v1/payroll/payouts/${id}`, { actor, method: 'PATCH', json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payouts'] }),
  });
}
export function useDeletePayout(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ApiError, string>({
    mutationFn: (id) => api(`/api/v1/payroll/payouts/${id}`, { actor, method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payouts'] }),
  });
}

export const MONTH_LABELS = [
  '1月',
  '2月',
  '3月',
  '4月',
  '5月',
  '6月',
  '7月',
  '8月',
  '9月',
  '10月',
  '11月',
  '12月',
];

// ---- V501 法定繳款追蹤 ----
export type RemitStatus = 'none' | 'unknown' | 'paid' | 'partial' | 'unpaid' | 'overdue';
export interface StatutoryRemitRow {
  ledger_entity_id: string;
  /** 所屬月份 (月初 YYYY-MM-01) */
  period: string;
  item: RemitItem;
  due_date: string;
  /** 薪資單算的 (只有預設主體、已定版的月份有) */
  computed: string | null;
  computed_employee: string | null;
  /** 繳款單金額 (人照單填); 有值就以它為準 */
  bill_amount: string | null;
  bill_note: string | null;
  expected: string | null;
  paid: string;
  /** 還在簽核 / 待付款的 */
  in_flight: string;
  status: RemitStatus;
  payments: {
    request_id: string;
    request_no: string | null;
    amount: string;
    status: string;
    paid_date: string | null;
  }[];
}
export interface StatutoryRemitUnlinked {
  request_id: string;
  request_no: string | null;
  gp: string | null;
  payee_name: string;
  category: string;
  /** 補標時當成哪個科目 (舊科目付給勞保局 / 健保署 → 勞健保勞退繳納) */
  remit_category: string;
  amount: string;
  status: string;
  paid_date: string | null;
}
export interface StatutoryRemitEntity {
  id: string;
  name: string;
  is_default: boolean;
  entity_kind: string;
  items: RemitItem[];
}
export interface StatutoryRemitData {
  year: number;
  today: string;
  /** 有追蹤項目的主體 (公司切換) */
  entities: StatutoryRemitEntity[];
  /** V503 設定追蹤項目用: 看得到的全部主體 */
  all_entities: StatutoryRemitEntity[];
  rows: StatutoryRemitRow[];
  unlinked: StatutoryRemitUnlinked[];
  /** V502 設成「不列入」的付款 (員工個人補繳之類) */
  excluded: StatutoryRemitUnlinked[];
}

export function useStatutoryRemit(actor: DevActorPreset, year: number) {
  return useQuery<StatutoryRemitData, ApiError>({
    queryKey: ['payroll-statutory-remit', actor, year],
    queryFn: () => api(`/api/v1/payroll/statutory-remit?year=${year}`, { actor }),
  });
}

export function useSaveRemitBill(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    {
      ledger_entity_id: number;
      period: string;
      item: RemitItem;
      bill_amount: number | null;
      note?: string | null;
    }
  >({
    mutationFn: (body) =>
      api('/api/v1/payroll/statutory-remit/bill', { actor, method: 'PUT', json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-statutory-remit'] }),
  });
}

export function useLinkRemitPayment(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { request_id: number; remit_period: string | null; remit_item: RemitItem | null }
  >({
    mutationFn: (body) =>
      api('/api/v1/payroll/statutory-remit/link', { actor, method: 'PUT', json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-statutory-remit'] }),
  });
}

export function useSaveRemitScope(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { ledger_entity_id: number; items: RemitItem[] }>({
    mutationFn: (body) =>
      api('/api/v1/payroll/statutory-remit/scope', { actor, method: 'PUT', json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-statutory-remit'] }),
  });
}

export function useExcludeRemitPayment(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { request_id: number; excluded: boolean }>({
    mutationFn: (body) =>
      api('/api/v1/payroll/statutory-remit/exclude', { actor, method: 'PUT', json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-statutory-remit'] }),
  });
}
